import type { BoardDto, CardDto, ColumnDto } from '@kanban/shared';
import { byPosition } from '@kanban/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { clientId } from './api.js';
import { boardKey } from './boards.js';

/**
 * WebSocket-Client (SPEC 7.1).
 *
 * Eingehende Events schreiben gezielt in den Query-Cache statt einen Refetch
 * auszuloesen — sonst wuerde jede Bewegung eines anderen Nutzers das ganze
 * Board neu laden. Nach einem Reconnect wird dagegen vollstaendig nachgeladen:
 * Event-Replay waere fuer diese Datenmenge unverhaeltnismaessig.
 */

type EventType =
  | 'card.created'
  | 'card.updated'
  | 'card.moved'
  | 'card.archived'
  | 'column.created'
  | 'column.updated'
  | 'column.archived'
  | 'board.updated'
  /** Anmahnung — kommt nur bei den Gemeinten an, nicht im ganzen Board. */
  | 'card.nudged'
  /** Neuer, geaenderter oder geloeschter Kommentar an einer Karte (SPEC 7.1). */
  | 'comment.created'
  /** Wer gerade im Board ist und welche Karte er offen hat (SPEC 7.3). */
  | 'presence.state';

interface RealtimeEvent {
  type: EventType | 'subscribed';
  boardId: string;
  payload: unknown;
  actorId: string;
  actorName: string;
  ts: number;
}

const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;

export type ConnectionState = 'verbunden' | 'getrennt';

/** Ein Anwesender und die Karte, an der er gerade sitzt (SPEC 7.3). */
export interface PresenceEntry {
  userId: string;
  cardId: string | null;
}

function withoutCard(columns: ColumnDto[], cardId: string): ColumnDto[] {
  return columns.map((column) => ({
    ...column,
    cards: column.cards.filter((card) => card.id !== cardId),
  }));
}

/** Karte einsortieren; sie kann die Spalte gewechselt haben. */
function upsertCard(columns: ColumnDto[], card: CardDto): ColumnDto[] {
  return withoutCard(columns, card.id).map((column) =>
    column.id === card.columnId
      ? { ...column, cards: [...column.cards, card].sort(byPosition) }
      : column,
  );
}

function applyEvent(board: BoardDto, event: RealtimeEvent): BoardDto {
  switch (event.type) {
    case 'card.created':
    case 'card.updated':
    case 'card.moved':
      return { ...board, columns: upsertCard(board.columns, event.payload as CardDto) };

    case 'card.archived': {
      const { id } = event.payload as { id: string };
      return { ...board, columns: withoutCard(board.columns, id) };
    }

    case 'column.created': {
      const column = event.payload as ColumnDto;
      if (board.columns.some((c) => c.id === column.id)) return board;
      return { ...board, columns: [...board.columns, column].sort(byPosition) };
    }

    case 'column.updated': {
      const incoming = event.payload as ColumnDto;
      return {
        ...board,
        columns: board.columns
          // Karten aus dem lokalen Stand behalten: das Spalten-DTO traegt sie
          // zwar mit, aber der lokale Stand kann frischer sein.
          .map((column) =>
            column.id === incoming.id ? { ...incoming, cards: column.cards } : column,
          )
          .sort(byPosition),
      };
    }

    case 'column.archived': {
      const { id } = event.payload as { id: string };
      return { ...board, columns: board.columns.filter((column) => column.id !== id) };
    }

    case 'comment.created': {
      // Nur der Zaehler auf der Karte. Die Beitraege selbst holt das
      // Kartendetail nach, und nur wenn es offen ist.
      const { cardId, commentCount } = event.payload as { cardId: string; commentCount?: number };
      if (typeof commentCount !== 'number') return board;
      return {
        ...board,
        columns: board.columns.map((column) => ({
          ...column,
          cards: column.cards.map((card) => (card.id === cardId ? { ...card, commentCount } : card)),
        })),
      };
    }

    case 'board.updated': {
      const incoming = event.payload as BoardDto;
      // `boardRole` stammt aus der Sicht des Ausloesers und gilt fuer den
      // Empfaenger nicht — sonst wuerde ein Admin, der etwas aendert, allen
      // anderen seine Rolle unterschieben. Spalten bleiben lokal, der lokale
      // Stand kann frischer sein.
      return { ...incoming, boardRole: board.boardRole, columns: board.columns };
    }

    default:
      return board;
  }
}

/**
 * Verbindung fuer das offene Board. Baut sich bei Verbindungsverlust mit
 * exponentiellem Backoff neu auf (1 s → max. 30 s).
 */
export interface RealtimeState {
  connection: ConnectionState;
  /** Wer sonst noch im Board ist — ohne einen selbst. */
  presence: PresenceEntry[];
  /** Melden, welche Karte gerade offen ist. `null` = keine. */
  reportFocus: (cardId: string | null) => void;
}

export function useRealtime(
  boardId: string | undefined,
  /**
   * Die Nutzlast kommt mit: fuer eine Anmahnung braucht der Hinweis den
   * Kartentitel, und den aus dem Cache nachzuschlagen ginge schief, sobald
   * die Karte gerade nicht geladen ist.
   */
  onRemoteChange?: (event: { type: EventType; actorName: string; payload: unknown }) => void,
): RealtimeState {
  const queryClient = useQueryClient();
  const [state, setState] = useState<ConnectionState>('getrennt');
  const [presence, setPresence] = useState<PresenceEntry[]>([]);
  /*
   * Der offene Socket in einer Ref: `reportFocus` darf sich nicht bei jedem
   * Zustandswechsel neu bilden, sonst meldet ein Effekt im Board bei jedem
   * Rendern erneut denselben Fokus.
   */
  const socketRef = useRef<WebSocket | null>(null);
  const pendingFocus = useRef<string | null>(null);
  // Über Ref, damit ein neuer Callback nicht die Verbindung neu aufbaut.
  const notify = useRef(onRemoteChange);
  notify.current = onRemoteChange;

  useEffect(() => {
    if (!boardId) return;

    let socket: WebSocket | null = null;
    let backoff = INITIAL_BACKOFF_MS;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let closedByUs = false;
    /** Erst nach einem echten Reconnect nachladen, nicht beim ersten Verbinden. */
    let hadConnection = false;

    const connect = () => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      socket = new WebSocket(`${protocol}//${window.location.host}/ws`);
      socketRef.current = socket;

      socket.onopen = () => {
        socket?.send(JSON.stringify({ type: 'subscribe', boardId, clientId }));
        // Nach einem Reconnect weiss der Server nichts mehr von der offenen
        // Karte — also gleich wieder melden.
        if (pendingFocus.current) {
          socket?.send(JSON.stringify({ type: 'focus', cardId: pendingFocus.current }));
        }
      };

      socket.onmessage = (message: MessageEvent<string>) => {
        let event: RealtimeEvent;
        try {
          event = JSON.parse(message.data) as RealtimeEvent;
        } catch {
          return;
        }

        if (event.type === 'subscribed') {
          setState('verbunden');
          backoff = INITIAL_BACKOFF_MS;
          if (hadConnection) {
            // Nach Reconnect vollstaendig nachladen statt Events nachzuspielen
            // (SPEC 7.1) — waehrend der Trennung verpasste Aenderungen sind
            // sonst unsichtbar.
            void queryClient.invalidateQueries({ queryKey: boardKey(boardId) });
          }
          hadConnection = true;
          return;
        }

        if (event.boardId !== boardId) return;

        if (event.type === 'presence.state') {
          setPresence(event.payload as PresenceEntry[]);
          return;
        }

        queryClient.setQueryData<BoardDto>(boardKey(boardId), (board) =>
          board ? applyEvent(board, event) : board,
        );

        // Ein offenes Kartendetail zeigt die Beitraege selbst — die stehen
        // nicht im Board-Cache und muessen nachgeladen werden.
        if (event.type === 'comment.created') {
          const { cardId } = event.payload as { cardId?: string };
          if (cardId) void queryClient.invalidateQueries({ queryKey: ['card', cardId] });
        }

        notify.current?.({
          type: event.type as EventType,
          actorName: event.actorName,
          payload: event.payload,
        });
      };

      socket.onclose = () => {
        setState('getrennt');
        if (closedByUs) return;
        reconnectTimer = setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
      };

      socket.onerror = () => socket?.close();
    };

    connect();

    return () => {
      closedByUs = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      socket?.close();
      socketRef.current = null;
      setPresence([]);
    };
  }, [boardId, queryClient]);

  const reportFocus = useCallback((cardId: string | null) => {
    pendingFocus.current = cardId;
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'focus', cardId }));
    }
  }, []);

  return { connection: state, presence, reportFocus };
}
